import React, { useState, useCallback } from 'react';
import { owaspTop10, getRank } from './data/owaspData';

import TitleScreen from './components/screens/TitleScreen';
import BountySelectScreen from './components/screens/BountySelectScreen';
import DossierScreen from './components/screens/DossierScreen';
import HuntScreen from './components/screens/HuntScreen';
import { GameOverScreen, VictoryScreen } from './components/screens/ResultScreens';

const STATES = {
  TITLE: 'title',
  BOUNTY_SELECT: 'bounty_select',
  DOSSIER: 'dossier',
  HUNT: 'hunt',
  GAME_OVER: 'game_over',
  VICTORY: 'victory'
};

function App() {
  const [state, setState] = useState(STATES.TITLE);
  const [selectedBounties, setSelectedBounties] = useState([]);
  const [currentBountyIndex, setCurrentBountyIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [credibility, setCredibility] = useState(100);
  const [mitigatedBounties, setMitigatedBounties] = useState([]);
  const [totalStats, setTotalStats] = useState({
    threatsKilled: 0,
    bossesDefeated: 0,
    maxCombo: 0
  });

  const currentVuln = selectedBounties[currentBountyIndex];

  const startGame = useCallback(() => {
    setScore(0);
    setCredibility(100);
    setTotalStats({ threatsKilled: 0, bossesDefeated: 0, maxCombo: 0 });
    setState(STATES.BOUNTY_SELECT);
  }, []);

  const acceptContract = useCallback((bounties) => {
    setSelectedBounties(bounties);
    setCurrentBountyIndex(0);
    setState(STATES.DOSSIER);
  }, []);

  const startHunt = useCallback(() => {
    setState(STATES.HUNT);
  }, []);

  const abandonFromDossier = useCallback(() => {
    setState(STATES.GAME_OVER);
  }, []);

  const completeHunt = useCallback((stats) => {
    if (stats.died || stats.quit) {
      setState(STATES.GAME_OVER);
      return;
    }

    // Mark bounty as mitigated
    if (currentVuln && !mitigatedBounties.includes(currentVuln.id)) {
      setMitigatedBounties(prev => [...prev, currentVuln.id]);
    }

    setTotalStats(prev => ({
      threatsKilled: prev.threatsKilled + stats.threatsKilled,
      bossesDefeated: prev.bossesDefeated + 1,
      maxCombo: Math.max(prev.maxCombo, stats.maxCombo || 0)
    }));

    if (currentBountyIndex >= selectedBounties.length - 1) {
      // All bounties complete
      setState(STATES.VICTORY);
    } else {
      // Next bounty
      setCurrentBountyIndex(i => i + 1);
      setState(STATES.DOSSIER);
    }
  }, [currentBountyIndex, selectedBounties.length, currentVuln, mitigatedBounties]);

  const addScore = useCallback((pts) => {
    setScore(s => s + pts);
  }, []);

  const damageCredibility = useCallback((amt) => {
    setCredibility(c => {
      const newC = Math.max(0, c - amt);
      if (newC <= 0) {
        setState(STATES.GAME_OVER);
      }
      return newC;
    });
  }, []);

  const returnToTitle = useCallback(() => {
    setState(STATES.TITLE);
  }, []);

  const backToSelect = useCallback(() => {
    setState(STATES.BOUNTY_SELECT);
  }, []);

  const renderScreen = () => {
    switch (state) {
      case STATES.TITLE:
        return <TitleScreen onStart={startGame} />;

      case STATES.BOUNTY_SELECT:
        return (
          <BountySelectScreen
            vulns={owaspTop10}
            mitigatedBounties={mitigatedBounties}
            onAccept={acceptContract}
            onBack={returnToTitle}
          />
        );

      case STATES.DOSSIER:
        return (
          <DossierScreen
            vuln={currentVuln}
            level={currentBountyIndex + 1}
            totalLevels={selectedBounties.length}
            score={score}
            credibility={credibility}
            onContinue={startHunt}
            onAbandon={abandonFromDossier}
          />
        );

      case STATES.HUNT:
        return (
          <HuntScreen
            vuln={currentVuln}
            levelNum={currentBountyIndex + 1}
            totalLevels={selectedBounties.length}
            score={score}
            credibility={credibility}
            onComplete={completeHunt}
            addScore={addScore}
            damageCredibility={damageCredibility}
          />
        );

      case STATES.GAME_OVER:
        return (
          <GameOverScreen
            level={currentBountyIndex + 1}
            totalLevels={selectedBounties.length}
            score={score}
            stats={totalStats}
            rank={getRank(score)}
            onRestart={backToSelect}
            onTitle={returnToTitle}
          />
        );

      case STATES.VICTORY:
        return (
          <VictoryScreen
            score={score}
            stats={totalStats}
            rank={getRank(score)}
            onRestart={backToSelect}
            onTitle={returnToTitle}
          />
        );

      default:
        return <TitleScreen onStart={startGame} />;
    }
  };

  return <div className="game-container">{renderScreen()}</div>;
}

export default App;
